import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Menu, X, Search, MapPin, ChevronDown, User } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { SearchOverlay } from '@/components/SearchOverlay'
import { Logo } from '@/components/Logo'
import { type RouteKey } from '@/i18n/routes'

type DropdownItem = { labelKey: string; routeKey: RouteKey; defaultLabel: string }

type NavItem = { labelKey: string } & (
  | { type: 'route'; routeKey: RouteKey }
  | { type: 'hash'; href: string }
  | { type: 'dropdown'; routeKey: RouteKey; items: DropdownItem[] }
)

const productCategories: DropdownItem[] = [
  { labelKey: 'nav.productsNovita', routeKey: 'productsNovita', defaultLabel: 'Novità' },
  { labelKey: 'nav.productsCapelli', routeKey: 'productsHair', defaultLabel: 'Capelli' },
  { labelKey: 'nav.productsCorpo', routeKey: 'productsCorpo', defaultLabel: 'Corpo' },
  { labelKey: 'nav.productsViso', routeKey: 'productsViso', defaultLabel: 'Viso' },
  { labelKey: 'nav.productsFragranze', routeKey: 'productsFragranze', defaultLabel: 'Fragranze' },
  { labelKey: 'nav.productsUomo', routeKey: 'productsUomo', defaultLabel: 'Uomo' },
]

const storeSalonItems: DropdownItem[] = [
  { labelKey: 'nav.nashiStore', routeKey: 'nashiStore', defaultLabel: 'Nashi Store' },
  { labelKey: 'nav.nashiSalon', routeKey: 'salon', defaultLabel: 'Nashi Salon' },
  { labelKey: 'nav.nashiPartner', routeKey: 'nashiPartner', defaultLabel: 'Nashi Partner' },
]

const primaryNav: NavItem[] = [
  { labelKey: 'nav.products', type: 'dropdown', routeKey: 'products', items: productCategories },
  { labelKey: 'nav.salon', type: 'dropdown', routeKey: 'salon', items: storeSalonItems },
  { labelKey: 'nav.world', type: 'route', routeKey: 'world' },
  { labelKey: 'nav.welcomeBox', type: 'route', routeKey: 'welcomeBox' },
  { labelKey: 'nav.news', type: 'route', routeKey: 'news' },
]

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileOpen])

  return (
    <>
      {/* Utility bar — hidden on small screens */}
      <div
        aria-hidden={isScrolled}
        className={cn(
          'hidden lg:block w-full border-b border-bruno/10 transition-all duration-500',
          isScrolled
            ? 'h-0 overflow-hidden border-transparent opacity-0'
            : 'h-9 opacity-100'
        )}
      >
        <div className="nashi-container flex h-full items-center justify-between text-[11px] tracking-[0.16em] uppercase text-bruno-soft">
          <div className="flex items-center gap-6">
            <LocalizedLink to="contacts" className="hover:text-argan-deep transition-colors">
              {t('utility.contact', 'Contattaci')}
            </LocalizedLink>
            <LocalizedLink to="faq" className="hover:text-argan-deep transition-colors">
              FAQ
            </LocalizedLink>
            <a href="#newsletter" className="hover:text-argan-deep transition-colors">
              {t('utility.newsletter', 'Newsletter')}
            </a>
            <LocalizedLink to="storeLocator" className="flex items-center gap-1.5 hover:text-argan-deep transition-colors">
              <MapPin className="h-3 w-3" strokeWidth={1.5} />
              {t('utility.storeLocator', 'Trova un Salone')}
            </LocalizedLink>
          </div>
          <LanguageSelector variant="desktop" isScrolled={false} />
        </div>
      </div>

      {/* Main header */}
      <header
        className={cn(
          'sticky top-0 z-[1100] w-full transition-all duration-500 relative',
          isScrolled
            ? 'bg-cream/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(59,49,46,0.08)]'
            : 'bg-cream'
        )}
      >
        <nav
          aria-label={t('aria.mainNavigation', 'Main navigation')}
          className={cn(
            'nashi-container grid grid-cols-[auto_1fr_auto] items-center transition-all duration-500',
            isScrolled ? 'h-16' : 'h-20 md:h-24'
          )}
        >
          {/* Left: logo (desktop) + mobile toggle */}
          <div className="flex items-center">
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="lg:hidden flex items-center text-bruno"
              aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
              aria-expanded={isMobileOpen}
            >
              {isMobileOpen ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
            </button>
            <Logo isScrolled={isScrolled} className="hidden lg:flex" />
          </div>

          {/* Center: logo (mobile only) */}
          <Logo isScrolled={isScrolled} className="lg:hidden justify-center" />

          {/* Right nav */}
          <div className="hidden lg:flex items-center gap-8 lg:col-start-3 justify-self-end">
            {primaryNav.map((item) => (
              <NavLink key={item.labelKey} item={item} t={t} />
            ))}
            <LocalizedLink
              to="login"
              className="text-bruno hover:text-argan-deep transition-colors"
              aria-label={t('utility.account', 'Account')}
            >
              <User className="h-4 w-4" strokeWidth={1.5} />
            </LocalizedLink>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="text-bruno hover:text-argan-deep transition-colors"
              aria-label={t('aria.search', 'Cerca')}
            >
              <Search className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Mobile right: account + search */}
          <div className="lg:hidden col-start-3 justify-self-end flex items-center gap-4">
            <LocalizedLink
              to="login"
              className="text-bruno"
              aria-label={t('utility.account', 'Account')}
            >
              <User className="h-5 w-5" strokeWidth={1.5} />
            </LocalizedLink>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="text-bruno"
              aria-label={t('aria.search', 'Cerca')}
            >
              <Search className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </nav>
        <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      </header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-0 top-20 md:top-24 z-40 bg-cream overflow-y-auto"
          >
            <nav
              className="nashi-container flex flex-col py-12"
              aria-label={t('aria.mobileNavigation', 'Mobile navigation')}
            >
              {primaryNav.map((item, idx) => {
                if (item.type === 'dropdown') {
                  return (
                    <motion.div
                      key={item.labelKey}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 + idx * 0.06, duration: 0.5 }}
                      className="border-b border-bruno/10 py-6"
                    >
                      <div className="flex items-baseline justify-between text-bruno mb-4">
                        <LocalizedLink
                          to={item.routeKey}
                          onClick={() => setIsMobileOpen(false)}
                          className="font-display text-3xl"
                        >
                          {t(item.labelKey)}
                        </LocalizedLink>
                        <span className="nashi-numeral">{`0${idx + 1}`}</span>
                      </div>
                      <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
                        {item.items.map((sub) => (
                          <li key={sub.routeKey}>
                            <LocalizedLink
                              to={sub.routeKey}
                              onClick={() => setIsMobileOpen(false)}
                              className="block py-1 text-[12px] uppercase tracking-[0.18em] text-bruno-soft hover:text-argan-deep transition-colors"
                            >
                              {t(sub.labelKey, sub.defaultLabel)}
                            </LocalizedLink>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )
                }

                if (item.type === 'route') {
                  return (
                    <motion.div
                      key={item.labelKey}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 + idx * 0.06, duration: 0.5 }}
                    >
                      <LocalizedLink
                        to={item.routeKey}
                        onClick={() => setIsMobileOpen(false)}
                        className="group flex items-baseline justify-between border-b border-bruno/10 py-6 text-bruno"
                      >
                        <span className="font-display text-3xl">{t(item.labelKey)}</span>
                        <span className="nashi-numeral">{`0${idx + 1}`}</span>
                      </LocalizedLink>
                    </motion.div>
                  )
                }

                return (
                  <motion.a
                    key={item.labelKey}
                    href={item.href}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + idx * 0.06, duration: 0.5 }}
                    className="group flex items-baseline justify-between border-b border-bruno/10 py-6 text-bruno"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    <span className="font-display text-3xl">{t(item.labelKey)}</span>
                    <span className="nashi-numeral">{`0${idx + 1}`}</span>
                  </motion.a>
                )
              })}

              <div className="mt-12 grid grid-cols-2 gap-y-4 text-[11px] uppercase tracking-[0.16em] text-bruno-soft">
                <LocalizedLink to="contacts" onClick={() => setIsMobileOpen(false)}>
                  {t('utility.contact', 'Contattaci')}
                </LocalizedLink>
                <LocalizedLink to="faq" onClick={() => setIsMobileOpen(false)}>
                  FAQ
                </LocalizedLink>
                <LocalizedLink to="storeLocator" onClick={() => setIsMobileOpen(false)}>
                  {t('utility.storeLocator', 'Trova un Salone')}
                </LocalizedLink>
                <a href="#newsletter">{t('utility.newsletter', 'Newsletter')}</a>
              </div>

              <div className="mt-10 border-t border-bruno/10 pt-8">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function NavLink({
  item,
  t,
}: {
  item: NavItem
  t: TFunction
}) {
  const baseClasses =
    'group relative text-[11px] tracking-[0.18em] uppercase text-bruno hover:text-argan-deep transition-colors'

  const inner = (
    <>
      {t(item.labelKey)}
      <span className="absolute -bottom-1 left-1/2 h-px w-0 bg-argan transition-all duration-500 group-hover:left-0 group-hover:w-full" />
    </>
  )

  if (item.type === 'dropdown') {
    return <NavDropdown item={item} t={t} />
  }

  if (item.type === 'route') {
    return (
      <LocalizedLink to={item.routeKey} className={baseClasses}>
        {inner}
      </LocalizedLink>
    )
  }

  return (
    <a href={item.href} className={baseClasses}>
      {inner}
    </a>
  )
}

function NavDropdown({
  item,
  t,
}: {
  item: Extract<NavItem, { type: 'dropdown' }>
  t: TFunction
}) {
  const [isOpen, setIsOpen] = useState(false)

  const baseClasses =
    'group relative inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-bruno hover:text-argan-deep transition-colors'

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <LocalizedLink
        to={item.routeKey}
        className={baseClasses}
        onFocus={() => setIsOpen(true)}
      >
        {t(item.labelKey)}
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform duration-300',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
          strokeWidth={1.5}
        />
        <span className="absolute -bottom-1 left-1/2 h-px w-0 bg-argan transition-all duration-500 group-hover:left-0 group-hover:w-full" />
      </LocalizedLink>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
            role="menu"
          >
            <div className="min-w-[14rem] bg-cream border border-bruno/10 shadow-[0_8px_30px_-12px_rgba(59,49,46,0.18)]">
              <ul className="py-2">
                {item.items.map((sub, idx) => (
                  <li key={sub.routeKey} role="none">
                    <LocalizedLink
                      to={sub.routeKey}
                      role="menuitem"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center justify-between gap-6 px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-bruno hover:bg-cream-soft hover:text-argan-deep transition-colors"
                    >
                      <span>{t(sub.labelKey, sub.defaultLabel)}</span>
                      <span className="font-display italic text-argan-deep/60 text-xs tabular-nums">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
